<template>
  <div>
    <v-row :no-gutters="hideHeader" :class="{ 'px-3': !hideHeader }">
      <!-- Image Upload -->
      <v-col cols="12" class="grey lighten-4 round-corners" v-if="!hideHeader">
        <v-row no-gutters>
          <v-col cols="6" class="font-weight-bold">
            {{ title }}
          </v-col>
          <v-col cols="6" class="text-right">
            <v-btn
              small
              color="primary"
              :disabled="disabled"
              @click="show()"
              v-if="isEditing && !noUpload"
              >Añadir</v-btn
            >
          </v-col>
        </v-row>
      </v-col>
      <!-- Table -->
      <v-col cols="12">
        <v-data-table
          :headers="filteredHeaders"
          :items="items"
          :loading="loading"
          :hide-default-footer="hideFooter || externalFooter"
          class="elevation-0"
          :items-per-page="5"
          :page.sync="page"
          @page-count="pageCount = $event"
        >
          <!-- Top -->
          <template v-slot:top>
            <v-toolbar flat color="white" v-if="hideHeader">
              <span class="title">{{ title }}</span>
              <v-spacer />
              <v-btn
                small
                color="primary"
                :disabled="disabled"
                @click="show()"
                v-if="isEditing"
                >Añadir</v-btn
              >
            </v-toolbar>
          </template>
          <!-- Preview -->
          <template v-slot:item.preview="{ item }">
            <v-img
              v-if="isFileImage(item.name)"
              class="cursor-pointer my-2"
              :src="item.download_url"
              contain
              aspect-ratio="1"
              max-height="70px"
              @click="show('preview', item)"
            >
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0 grey lighten-5"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular indeterminate color="grey" />
                </v-row>
              </template>
            </v-img>
            <v-icon v-else>mdi-file</v-icon>
          </template>
          <!-- Actions -->
          <template v-slot:item.action="{ item }">
            <v-icon class="mr-2" @click="openFile(item.download_url)"
              >mdi-link</v-icon
            >
            <v-icon class="mr-2" @click="show('edit', item)" v-if="!hideEditor"
              >mdi-image-edit</v-icon
            >
            <v-icon @click="show('delete', item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
        <v-pagination
          v-model="page"
          :length="pageCount"
          v-if="externalFooter"
        ></v-pagination>
      </v-col>
    </v-row>

    <!--  Dialog -->
    <v-dialog v-model="dialog" persistent width="400px">
      <v-card v-if="dialog">
        <v-card-title primary-title> Añadir {{ typeTitle }} </v-card-title>
        <v-card-text>
          <v-row justify="space-around">
            <!-- Image-->
            <v-col cols="12">
              <v-file-input
                v-model="files"
                :error-messages="filesErrors"
                label="Archivo adjunto"
                accept="image/*"
                :hint="message"
                multiple
                persistent-hint
                show-size
                outlined
                @blur="$v.files.$touch()"
                @change="previewFiles()"
              ></v-file-input>
            </v-col>
            <!-- Image Preview -->
            <v-expand-transition>
              <v-col cols="12" v-if="images.length">
                <v-carousel hide-delimiters show-arrows-on-hover>
                  <v-carousel-item
                    v-for="image in images"
                    :key="image.id"
                    reverse-transition="fade-transition"
                    transition="fade-transition"
                  >
                    <v-img :src="image" contain aspect-ratio="1">
                      <template v-slot:placeholder>
                        <v-row
                          class="fill-height ma-0 grey lighten-5"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular indeterminate color="grey" />
                        </v-row>
                      </template>
                    </v-img>
                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-expand-transition>
          </v-row>
        </v-card-text>
        <!-- Buttons -->
        <v-card-actions>
          <v-spacer />
          <v-btn text class="mr-4" @click="close()">Cancelar</v-btn>
          <v-btn :disabled="invalid" color="primary" @click="save()">Añadir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image Preview -->
    <image-preview
      v-model="previewDialog"
      :item="item"
      @close="close('preview')"
    />

    <!-- Image Editor -->
    <image-editor
      v-model="editDialog"
      :item="item"
      @close="close('edit')"
      @save="save($event)"
    />

    <!-- Confirmation -->
    <confirmation
      v-model="deleteDialog"
      title="Eliminar imágen"
      text="Desea eliminar la imágen"
      :item="item.name"
      @close="close('delete')"
      @confirm="deleteItem()"
    />
  </div>
</template>

<script>
import ImagePreview from "@/components/ImagePreview";
import ImageEditor from "@/components/ImageEditor";
import Confirmation from "@/components/Confirmation";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

export default {
  name: "images-upload",
  props: {
    items: Array,
    loading: Boolean,
    title: String,
    typeTitle: { type: String, default: "Imágen" },
    fileSizeMB: { type: Number, default: 20 },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    noUpload: { type: Boolean, default: false },
    hideEditor: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: true },
    hideHeader: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    externalFooter: { type: Boolean, default: false },
    acceptAllFiles: { type: Boolean, default: false }
  },
  components: {
    ImagePreview,
    ImageEditor,
    Confirmation
  },
  mixins: [validationMixin],
  validations: {
    files: { required }
  },
  data: () => ({
    page: 1,
    pageCount: 0,
    dialog: false,
    deleteDialog: false,
    previewDialog: false,
    editDialog: false,
    index: -1,
    item: {},
    files: [],
    images: [],
    imageExtensions: [".jpge", ".jpg", ".png", ".gif"],
    headers: [
      { text: "Archivo", value: "preview", align: "center" },
      { text: "Nombre", value: "name", align: "center" },
      { text: "Acción", value: "action", sortable: false, align: "center" }
    ]
  }),
  watch: {
    dialog(val) {
      val || this.close();
    },
  },
  computed: {
    isNew() {
      return this.index > -1 ? "Modificar" : "Añadir";
    },
    invalid() {
      return (
        (this.$v.$invalid && this.$v.$dirty) ||
        (!this.fileSizeRule || !this.fileTypeRule)
      );
    },
    isWorkOrder() {
      return this.$route.name.includes("order");
    },
    message() {
      return this.fileSizeMB
        ? "El tamaño de los archivos debe ser menor a " +
            this.fileSizeMB +
            " MB."
        : "";
    },
    filteredHeaders() {
      return this.headers.filter(
        element =>
          (this.isEditing && element.value === "action") ||
          element.value !== "action"
      );
    },
    filesErrors() {
      const errors = [];
      if (!this.$v.files.$dirty) return errors;
      !this.$v.files.required && errors.push("Obligatorio.");
      !this.fileSizeRule && errors.push(this.message);
      !this.fileTypeRule && errors.push("Solo acepta imágenes.");
      return errors;
    },
    fileSizeRule() {
      let size = 0
      this.files.forEach(e => size += e.size)
      return this.fileSizeMB > 0
        ? size < this.fileSizeMB * 1000000
        : true;
    },
    fileTypeRule() {
      return !this.acceptAllFiles
        ? !this.files.some(e => !e.type.includes("image/"))
        : true;
    },
  },
  methods: {
    previewFiles() {
      const images = []
      if (!this.files.some(e => !e.type.includes("image/"))) {
        for (let i=0; i < this.files.length; i++) {
          const element = this.files[i]
          const reader = new FileReader();
          reader.onload = () => (images.push(reader.result));
          reader.readAsDataURL(element);
        }
      }
      this.images = images
    },
    openFile(download_url) {
      window.open(download_url, "_blank");
    },
    isFileImage(name) {
      let isImage = false;
      const file = name.toLowerCase();
      for (let i = 0; i < this.imageExtensions.length; i++) {
        const element = this.imageExtensions[i];
        if (file.includes(element)) isImage = true;
      }
      return this.isWorkOrder ? isImage : true;
    },
    show(dialog, item) {
      if (dialog === "preview") {
        this.item = item;
        this.previewDialog = true;
      } else if (dialog === "edit") {
        this.item = item;
        this.editDialog = true;
      } else if (dialog === "delete") {
        this.item = item;
        this.deleteDialog = true;
      } else this.dialog = true;
    },
    close(dialog) {
      if (dialog === "preview") {
        this.previewDialog = false;
      } else if (dialog === "edit") {
        this.editDialog = false;
      } else if (dialog === "delete") {
        this.deleteDialog = false;
      } else {
        this.dialog = false;
        this.index = -1;
      }
      this.item = {};
      this.images = []
      this.file = null;
      this.$v.$reset();
    },
    deleteItem() {
      this.$emit("delete", this.item);
      this.close("delete");
    },
    save(editedFile) {
      if (editedFile) {
        this.$emit("save", editedFile);
        this.close();
      } else {
        this.$v.$touch();
        if (!this.$v.$invalid) {
          const files = this.multiple ? this.files : this.files[0]
          this.$emit("save", files, true);
          this.close();
        }
      }
    }
  },
  created() {
    this.list = this.items;
  }
};
</script>
